import {Fragment, useMemo} from 'react'
import {
  buildCellTypes,
  CELLS_PER_DAY,
  cellIndexToMinutes,
  isDayFullyBlocked,
  isRunStart,
  ISO_DAYS,
  GridSlot,
  minutesToLabel,
  OpenBand,
  PaintTool,
  ISO_DAY_LABELS,
  isoDayOf,
  SLOT_FILL_STYLE,
  SLOT_TYPE_LABELS,
  slotAt,
  STEP_MINUTES,
} from '../helpers/availability'
import {TableGrid} from '../styles/styles'
import useSyncScroll from '../hooks/useSyncScroll'
import {AvailabilityDayHeader} from './AvailabilityDayHeader'
import {useAvailabilityPaint} from './useAvailabilityPaint'

const CELL_HEIGHT = 22
// Week.tsx pins its header row to an explicit headerHeight; this uses a floor
// instead so the row still grows if the day-off label ever wraps — TableGrid is
// `overflow-y: hidden`, so anything it can't fit is clipped rather than scrolled.
const HEADER_MIN_HEIGHT = 68

interface Props {
  slots: GridSlot[]
  tool: PaintTool
  use12Hour?: boolean
  disabled?: boolean
  /**
   * Location opening hours, shaded as context. Availability is not limited to
   * them. A day can carry more than one band — an overnight close puts the
   * previous day's tail at the top of this one.
   */
  openBands?: Record<number, OpenBand[]>
  onPaint: (dayOfWeek: number, startMinute: number, endMinute: number) => void
  onToggleDay: (dayOfWeek: number) => void
  onSlotClick?: (slot: GridSlot) => void
  /**
   * Dated mode: the real date each column stands for, in column order. The
   * grid still indexes columns 0..6 — a column is just a column — so nothing
   * below changes except what the headers say.
   */
  columnDates?: Date[]
  /**
   * The standing weekly pattern, drawn faded beneath `slots`, with the hours
   * the dated entries cover already removed. Dated mode only: it is what the
   * week still falls back to, and it is never editable — painting a faded
   * cell writes a dated entry that overrides just those hours.
   */
  underlaySlots?: GridSlot[]
}

/**
 * The 7 × 48 availability grid: one column per ISO weekday, one row per
 * half hour across the full 00:00–24:00 day.
 *
 * Unlike the schedule board this deliberately spans the whole day rather than
 * hours of operation — availability is a property of the person, so an
 * employee's Tuesday morning class matters whether or not the store is open
 * then. Opening hours are shaded as context only.
 *
 * Cells are tinted directly from a precomputed per-cell lookup rather than
 * absolutely positioned like TodayEvents: the paint model has no overlaps to
 * fan out, so there is nothing to stack.
 */
export const AvailabilityGrid = ({
  slots,
  tool,
  use12Hour = false,
  disabled,
  openBands,
  onPaint,
  onToggleDay,
  onSlotClick,
  columnDates,
  underlaySlots,
}: Props) => {
  const {headersRef, bodyRef} = useSyncScroll()
  const {isCellInPreview, handleMouseDown, handleMouseEnter} = useAvailabilityPaint({
    onCommit: onPaint,
    disabled,
  })

  const cellTypes = useMemo(() => buildCellTypes(slots), [slots])
  const underlayTypes = useMemo(
    () => (underlaySlots?.length ? buildCellTypes(underlaySlots) : null),
    [underlaySlots]
  )
  const rows = useMemo(() => Array.from({length: CELLS_PER_DAY}, (_, index) => index), [])

  // TableGrid paints .rs__cell from MUI's palette (white) and draws its borders
  // in MUI grey — both wrong under data-bs-theme=dark. Re-point them at the
  // Bootstrap variables, scoped to this grid so the schedule board is untouched.
  const themedGrid = {
    borderColor: 'var(--bs-border-color-translucent)',
    '& .rs__cell': {
      background: 'var(--bs-body-bg)',
      borderColor: 'var(--bs-border-color-translucent)',
    },
  } as const

  const isOpen = (dayOfWeek: number, minute: number) => {
    const bands = openBands?.[dayOfWeek]
    if (!bands || !bands.length) return true // unknown hours → don't shade
    return bands.some((band) => minute >= band.startMinute && minute < band.endMinute)
  }

  return (
    <>
      <TableGrid days={ISO_DAYS.length} ref={headersRef} sticky='1' sx={themedGrid}>
        <span
          className='rs__cell rs__header rs__time bg-light'
          style={{minHeight: HEADER_MIN_HEIGHT}}
        />
        {ISO_DAYS.map((day) => (
          <span
            key={day}
            className='rs__cell rs__header bg-light'
            style={{minHeight: HEADER_MIN_HEIGHT}}
          >
            <AvailabilityDayHeader
              dayOfWeek={day}
              date={columnDates?.[day]}
              blocked={isDayFullyBlocked(slots, day)}
              disabled={disabled}
              onToggle={onToggleDay}
            />
          </span>
        ))}
      </TableGrid>

      <TableGrid days={ISO_DAYS.length} ref={bodyRef} sx={themedGrid}>
        {rows.map((rowIndex) => {
          const minute = cellIndexToMinutes(rowIndex)
          const onTheHour = minute % 60 === 0
          return (
            <Fragment key={rowIndex}>
              <span
                className='rs__cell rs__header rs__time bg-light text-gray-600'
                style={{height: CELL_HEIGHT}}
              >
                {/* Label the hour only — a label every half hour is noise. */}
                <span className='fs-8'>{onTheHour ? minutesToLabel(minute, use12Hour) : ''}</span>
              </span>

              {ISO_DAYS.map((day) => {
                const cells = cellTypes.get(day)!
                const slotType = cells[rowIndex]
                const inPreview = isCellInPreview(day, rowIndex)
                const covered = slotType !== null
                const slot = covered ? slotAt(slots, day, minute) : undefined
                const closed = !isOpen(day, minute)

                // No height here: TableGrid's `.rs__cell > button {height:100%}`
                // fills the row. Setting one made every button 1px shorter than
                // its wrapper (which adds its own bottom border), leaving an
                // unpainted strip between painted cells. For the same reason the
                // hour rule is an inset shadow, not a border — it must not
                // change the box.
                const style: React.CSSProperties = {
                  padding: 0,
                  border: 0,
                  width: '100%',
                  cursor: disabled ? 'default' : 'pointer',
                  backgroundColor: 'var(--bs-body-bg)',
                  ...(covered ? SLOT_FILL_STYLE[slotType!] : {}),
                }
                // The standing pattern, at a fraction of its strength, and
                // only where this date says nothing of its own — the dated
                // entry has already cut its own hours out of the underlay, so
                // the two never stack in one cell.
                const underlayType = covered ? null : underlayTypes?.get(day)?.[rowIndex] ?? null
                if (underlayType) {
                  Object.assign(style, SLOT_FILL_STYLE[underlayType])
                  style.opacity = 0.35
                }
                if (closed && !covered && !underlayType) {
                  // Tied to the body COLOUR, not a fixed grey, so the band stays
                  // a subtle contrast in both themes.
                  style.backgroundColor = 'rgba(var(--bs-body-color-rgb), 0.07)'
                }
                if (onTheHour) {
                  style.boxShadow = 'inset 0 1px 0 var(--bs-border-color-translucent)'
                }
                if (inPreview) {
                  style.outline = '2px solid var(--bs-primary)'
                  style.outlineOffset = '-2px'
                }

                const title = underlayType
                  ? `${SLOT_TYPE_LABELS[underlayType]} every ${
                      ISO_DAY_LABELS[columnDates ? isoDayOf(columnDates[day]) : day]
                    } — paint to override just this date`
                  : slot
                  ? `${SLOT_TYPE_LABELS[slot.slot_type]} ${minutesToLabel(
                      slot.start_minute,
                      use12Hour
                    )}–${minutesToLabel(slot.end_minute, use12Hour)}${
                      slot.note ? ` — ${slot.note}` : ''
                    }`
                  : `Available ${minutesToLabel(minute, use12Hour)}`

                return (
                  <div key={day} className='rs__cell' style={{height: CELL_HEIGHT}}>
                    <button
                      type='button'
                      data-day={day}
                      data-cell={rowIndex}
                      data-minute={minute}
                      data-slot-type={slotType ?? ''}
                      // The closed shading is a background colour built from a
                      // CSS variable, which jsdom drops on parse — this is the
                      // only handle a test (or a bug report) has on it.
                      data-closed={closed ? 'true' : undefined}
                      data-testid={`availability-cell-${day}-${rowIndex}`}
                      aria-label={title}
                      title={title}
                      disabled={disabled}
                      style={style}
                      onMouseDown={(event) => handleMouseDown(event, day, rowIndex)}
                      // onMouseOver, not onMouseEnter: React's synthetic
                      // mouseEnter doesn't bubble reliably while a button is
                      // held down, which is exactly when a drag needs it
                      // (same reason Cell.tsx binds it this way).
                      onMouseOver={() => handleMouseEnter(day, rowIndex)}
                      onDoubleClick={() => slot && onSlotClick?.(slot)}
                    >
                      {slot && isRunStart(cells, rowIndex) && (
                        // gray-900 flips with the theme, so the label stays
                        // readable on the tint in both light and dark. The
                        // body-bg halo lifts it off the hatch/dot patterns,
                        // which otherwise break up the glyphs.
                        <span
                          className='fs-8 fw-bold text-gray-900 px-1 d-block text-truncate'
                          style={{
                            lineHeight: `${CELL_HEIGHT}px`,
                            textAlign: 'left',
                            textShadow:
                              '0 0 3px var(--bs-body-bg), 0 0 3px var(--bs-body-bg), 0 0 2px var(--bs-body-bg)',
                            letterSpacing: '0.01em',
                          }}
                        >
                          {SLOT_TYPE_LABELS[slot.slot_type]}
                        </span>
                      )}
                    </button>
                  </div>
                )
              })}
            </Fragment>
          )
        })}
      </TableGrid>
      <div className='fs-8 text-gray-500 mt-2'>
        Drag over the grid to paint with the selected tool. Rows are {STEP_MINUTES}-minute steps;
        double-click a painted block to add a note.
      </div>
    </>
  )
}
